import { useEffect, useMemo, useState } from "react";
import {
    FaArchive, FaTrash, FaUndo, FaBuilding, FaFolderOpen, FaLayerGroup,
    FaBook, FaBookOpen, FaListOl, FaKey, FaFileAlt, FaServer, FaCode,
    FaStickyNote, FaLink, FaCodeBranch, FaTasks, FaShieldAlt, FaHeart, FaShoppingBag, FaAddressBook, FaColumns, FaPaperclip,
} from "react-icons/fa";
import { COMPANIES_CONSTANTS, VAULT_CONSTANTS } from "../../_Companies/_MODULES/_API";
import {
    PROJECTS_CONSTANTS, SUBPROJECTS_CONSTANTS, LOGBOOK_CONSTANTS, GUIDES_CONSTANTS,
    CREDENTIALS_CONSTANTS, DOCUMENTS_CONSTANTS, ENVIRONMENTS_CONSTANTS,
    SNIPPETS_CONSTANTS, DRAFTS_CONSTANTS, LINKS_CONSTANTS, CHANGES_CONSTANTS, TODOS_CONSTANTS,
    STEP_GROUPS_CONSTANTS, STEPS_CONSTANTS, KANBAN_COLUMNS_CONSTANTS, KANBAN_CARDS_CONSTANTS, KANBAN_CARD_FILES_CONSTANTS,
} from "../../_Projects/_MODULES/_API";
import { PERSONAL_VAULT_CONSTANTS } from "../../_PersonalVault/_MODULES/_API";
import { CONTACTS_CONSTANTS, CONTACT_GROUP_CONSTANTS } from "../../_Contacts/_MODULES/_API";
import { NOTEBOOK_COLLECTION_CONSTANTS, NOTEBOOK_ENTRY_CONSTANTS } from "../../_Notebook/_MODULES/_API";
import { STICKY_NOTES_CONSTANTS } from "../../_StickyNotes/_MODULES/_API";
import { WISHLIST_CATEGORY_CONSTANTS, WISHLIST_ITEM_CONSTANTS } from "../../_Wishlist/_MODULES/_API";
import { MANUALS_CONSTANTS } from "../../_Manuals/_MODULES/_API";
import { COURSES_CONSTANTS } from "../../_Courses/_MODULES/_API";
import { LIBRARY_CONSTANTS } from "../../_Library/_MODULES/_API";
import { TAGS_CONSTANTS } from "../../_Tags/_MODULES/_API";
import { SAVINGS_CONSTANTS } from "../../_Savings/_MODULES/_API";
import { MONTHLY_FINANCE_CONSTANTS } from "../../_MonthlyExpenses/_MODULES/_API";
import { SNIPPETS_CONSTANTS as CODE_REPOSITORY_CONSTANTS } from "../../_Snippets/_MODULES/_API";
import { CENTER_CONSTANTS } from "../../_Center/_MODULES/_API";
import { ViewLayout } from "../../../../_UI/ViewLayout";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { useDateRange, onMatchesRange } from "../../../../_UI/useDateRange";
import { PageHeader } from "../../../../../_CORE/APP/Templates/_UI/PageHeader/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { Card, EmptyState, Skeleton, Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { API_CORE } from "../../../../../_CORE/API/ApiCORE";
import { onApiAction } from "../../../../_UI/onApiAction";
import { AUTH_CONSTANTS } from "../../../Public/_Auth/_MODULES/_API";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { FieldInput } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import "../_UI/_Styles/Archive.css";

const onFormatDate = (value) => (value
    ? new Date(value).toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" })
    : "");

const onAsArray = (value) => Array.isArray(value) ? value : [];

const onDescendantIds = (items, id, parentField = "parentId") => {
    const ids = new Set([String(id)]);
    let changed = true;
    while (changed) {
        changed = false;
        items.forEach((row) => {
            const parentId = row?.[parentField] ? String(row[parentField]) : "";
            if (!ids.has(String(row.id)) && parentId && ids.has(parentId)) {
                ids.add(String(row.id));
                changed = true;
            }
        });
    }
    return ids;
};

/*
    Quita localmente el registro archivado y, cuando el backend restaura/elimina
    una jerarquía completa, también limpia sus descendientes. Así Archivados no
    necesita volver a pedir toda la pantalla después de cada acción.
*/
const onRemoveArchiveCascade = (state, key, item) => {
    const next = Object.fromEntries(Object.entries(state ?? {}).map(([bucket, rows]) => [bucket, onAsArray(rows)]));
    const removeIds = (bucket, ids) => {
        const set = ids instanceof Set ? ids : new Set([String(ids)]);
        next[bucket] = onAsArray(next[bucket]).filter((row) => !set.has(String(row.id)));
    };

    if (key === "credentials") {
        const ids = onDescendantIds(onAsArray(next.credentials), item.id);
        removeIds("credentials", ids);
        next.credentialFiles = onAsArray(next.credentialFiles).filter((row) => !ids.has(String(row.credentialId)));
        return next;
    }
    if (key === "guidePages") {
        removeIds("guidePages", onDescendantIds(onAsArray(next.guidePages), item.id));
        return next;
    }
    if (key === "stepGroups") {
        removeIds("stepGroups", item.id);
        next.steps = onAsArray(next.steps).filter((row) => String(row.groupId) !== String(item.id));
        return next;
    }
    if (key === "kanbanColumns") {
        removeIds("kanbanColumns", item.id);
        const cardIds = new Set(onAsArray(next.kanbanCards).filter((row) => String(row.columnId) === String(item.id)).map((row) => String(row.id)));
        next.kanbanCards = onAsArray(next.kanbanCards).filter((row) => !cardIds.has(String(row.id)));
        next.kanbanCardFiles = onAsArray(next.kanbanCardFiles).filter((row) => !cardIds.has(String(row.cardId)));
        return next;
    }
    if (key === "kanbanCards") {
        removeIds("kanbanCards", item.id);
        next.kanbanCardFiles = onAsArray(next.kanbanCardFiles).filter((row) => String(row.cardId) !== String(item.id));
        return next;
    }
    if (key === "companyVaultGroups" || key === "personalVaultGroups") {
        const prefix = key.startsWith("company") ? "companyVault" : "personalVault";
        removeIds(`${prefix}Groups`, item.id);
        next[`${prefix}Entries`] = onAsArray(next[`${prefix}Entries`]).filter((row) => String(row.groupId) !== String(item.id));
        next[`${prefix}Files`] = onAsArray(next[`${prefix}Files`]).filter((row) => String(row.groupId) !== String(item.id));
        return next;
    }
    if (key === "companyVaultEntries" || key === "personalVaultEntries") {
        const prefix = key.startsWith("company") ? "companyVault" : "personalVault";
        const ids = onDescendantIds(onAsArray(next[`${prefix}Entries`]), item.id);
        removeIds(`${prefix}Entries`, ids);
        next[`${prefix}Files`] = onAsArray(next[`${prefix}Files`]).filter((row) => !ids.has(String(row.entryId)));
        return next;
    }
    if (key === "contactGroups") {
        removeIds("contactGroups", item.id);
        next.contacts = onAsArray(next.contacts).filter((row) => String(row.groupId) !== String(item.id));
        return next;
    }
    if (key === "wishlistCategories") {
        removeIds("wishlistCategories", item.id);
        next.wishlistItems = onAsArray(next.wishlistItems).filter((row) => String(row.categoryId) !== String(item.id));
        return next;
    }
    if (key === "notebookCollections") {
        removeIds("notebookCollections", item.id);
        next.notebookEntries = onAsArray(next.notebookEntries).filter((row) => String(row.collectionId) !== String(item.id));
        return next;
    }
    if (key === "libraryGroups") {
        removeIds("libraryGroups", item.id);
        next.libraryDocuments = onAsArray(next.libraryDocuments).filter((row) => String(row.groupId) !== String(item.id));
        return next;
    }
    if (key === "manualPages") {
        removeIds("manualPages", onDescendantIds(onAsArray(next.manualPages), item.id));
        return next;
    }
    if (key === "courseModules") {
        removeIds("courseModules", item.id);
        ["courseLessons", "courseTasks", "courseSchedule"].forEach((bucket) => {
            next[bucket] = onAsArray(next[bucket]).filter((row) => String(row.moduleId) !== String(item.id));
        });
        return next;
    }
    if (key === "snippetRepositories") {
        removeIds("snippetRepositories", item.id);
        next.snippetFolders = onAsArray(next.snippetFolders).filter((row) => String(row.repositoryId) !== String(item.id));
        next.snippetFiles = onAsArray(next.snippetFiles).filter((row) => String(row.repositoryId) !== String(item.id));
        return next;
    }
    if (key === "snippetFolders") {
        const ids = onDescendantIds(onAsArray(next.snippetFolders), item.id);
        removeIds("snippetFolders", ids);
        next.snippetFiles = onAsArray(next.snippetFiles).filter((row) => !row.folderId || !ids.has(String(row.folderId)));
        return next;
    }
    if (key === "centerRunbooks") {
        removeIds("centerRunbooks", item.id);
        next.centerRunbookSteps = onAsArray(next.centerRunbookSteps).filter((row) => String(row.runbookId) !== String(item.id));
        return next;
    }

    removeIds(key, item.id);
    return next;
};

export const ArchiveViewMain = () => {
    const [useSearch, setSearch] = useState("");
    const DATE_RANGE = useDateRange();
    const [pendingDelete, setPendingDelete] = useState(null);
    const [usePassword, setPassword] = useState("");
    const [usePasswordError, setPasswordError] = useState(null);
    const [useIsChecking, setIsChecking] = useState(false);
    const [useExtraArchive, setExtraArchive] = useState({});
    const [useExtraLoading, setExtraLoading] = useState(true);

    const { actionMutate: onDeleteResource } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 0,
    });

    const {
        apiGet: onGetCompanies, useApiData: COMPANIES, useApiLoading: LOADING_COMPANIES, mutateLocal: mutateCompanies,
    } = useApiQuery({ minTimeQuery: 200, enablePagination: false, startLoading: true });
    const {
        apiGet: onGetProjects, useApiData: PROJECTS, useApiLoading: LOADING_PROJECTS, mutateLocal: mutateProjects,
    } = useApiQuery({ minTimeQuery: 200, enablePagination: false });
    const {
        apiGet: onGetSubProjects, useApiData: SUBPROJECTS, useApiLoading: LOADING_SUBPROJECTS, mutateLocal: mutateSubProjects,
    } = useApiQuery({ minTimeQuery: 200, enablePagination: false });
    const {
        apiGet: onGetLogbook, useApiData: LOGBOOK, useApiLoading: LOADING_LOGBOOK, mutateLocal: mutateLogbook,
    } = useApiQuery({ minTimeQuery: 200, enablePagination: false });
    const {
        apiGet: onGetManuals, useApiData: MANUALS, useApiLoading: LOADING_MANUALS, mutateLocal: mutateManuals,
    } = useApiQuery({ minTimeQuery: 200, enablePagination: false });
    const {
        apiGet: onGetGuides, useApiData: GUIDES, useApiLoading: LOADING_GUIDES, mutateLocal: mutateGuides,
    } = useApiQuery({ minTimeQuery: 200, enablePagination: false });
    const {
        apiGet: onGetCourses, useApiData: COURSES, useApiLoading: LOADING_COURSES, mutateLocal: mutateCourses,
    } = useApiQuery({ minTimeQuery: 200, enablePagination: false });

    const onLoadExtra = async () => {
        setExtraLoading(true);
        const response = await API_CORE.SERVICES.GET_RESOURCES("/archive/overview");
        const data = response?.data?.data ?? response?.data ?? null;
        if (data && typeof data === "object" && !Array.isArray(data)) setExtraArchive(data);
        setExtraLoading(false);
    };

    const onLoadAll = () => {
        onGetCompanies({ endpoint: COMPANIES_CONSTANTS.ARCHIVE_LIST });
        onGetProjects({ endpoint: PROJECTS_CONSTANTS.ARCHIVE_LIST });
        onGetSubProjects({ endpoint: SUBPROJECTS_CONSTANTS.ARCHIVE_LIST });
        onGetLogbook({ endpoint: LOGBOOK_CONSTANTS.ARCHIVE_LIST });
        onGetManuals({ endpoint: MANUALS_CONSTANTS.ARCHIVE_LIST });
        onGetGuides({ endpoint: GUIDES_CONSTANTS.ARCHIVE_LIST });
        onGetCourses({ endpoint: COURSES_CONSTANTS.ARCHIVE_LIST });
        onLoadExtra();
    };

    useEffect(() => { onLoadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onRestore = async (group, item) => {
        const extraSnapshot = group.isExtra ? useExtraArchive : null;
        if (group.isExtra) setExtraArchive((current) => onRemoveArchiveCascade(current, group.key, item));
        else group.mutate({ id: item.id, remove: true });

        const RESPONSE = await onApiAction({
            method: "PATCH", endpoint: group.restore, pathParams: { id: item.id }, successMessage: "Restaurado",
        });

        if (!RESPONSE.success) {
            if (group.isExtra) setExtraArchive(extraSnapshot);
            else group.mutate({ insert: item });
        }
    };

    const onRequestPassword = async (group, item) => {
        const IS_CONFIRMED = await confirm.danger(
            "Eliminar definitivamente",
            `Se elimina "${item.name}" y no se puede deshacer.${group.warning ? ` ${group.warning}` : ""}`,
            { confirmText: "Eliminar" },
        );
        if (!IS_CONFIRMED) return;

        setPendingDelete({ group, item });
        setPassword("");
        setPasswordError(null);
    };

    const onConfirmDelete = async () => {
        if (!usePassword.trim()) {
            setPasswordError("Escribí tu contraseña");
            return;
        }

        setIsChecking(true);
        const CHECK = await onApiAction({
            method: "POST", endpoint: AUTH_CONSTANTS.VERIFY_PASSWORD,
            data: { password: usePassword }, isToastError: false,
        });

        if (!CHECK.success || !CHECK.data?.isValid) {
            setIsChecking(false);
            setPasswordError("La contraseña no es correcta");
            return;
        }

        const { group, item } = pendingDelete;
        const extraSnapshot = group.isExtra ? useExtraArchive : null;
        if (group.isExtra) setExtraArchive((current) => onRemoveArchiveCascade(current, group.key, item));
        else group.mutate({ id: item.id, remove: true });

        const RESPONSE = await onDeleteResource({
            method: "DELETE", endpoint: group.remove, id: item.id,
            successMessage: "Eliminado definitivamente",
        });

        setIsChecking(false);
        setPendingDelete(null);
        if (!RESPONSE.success) {
            if (group.isExtra) setExtraArchive(extraSnapshot);
            else group.mutate({ insert: item });
        }
    };

    const EXTRA = useMemo(() => {
        const source = useExtraArchive ?? {};
        const credentialIds = new Set(onAsArray(source.credentials).map((row) => String(row.id)));
        const companyGroupIds = new Set(onAsArray(source.companyVaultGroups).map((row) => String(row.id)));
        const companyEntryIds = new Set(onAsArray(source.companyVaultEntries).map((row) => String(row.id)));
        const personalGroupIds = new Set(onAsArray(source.personalVaultGroups).map((row) => String(row.id)));
        const personalEntryIds = new Set(onAsArray(source.personalVaultEntries).map((row) => String(row.id)));
        const wishlistCategoryIds = new Set(onAsArray(source.wishlistCategories).map((row) => String(row.id)));
        const contactGroupIds = new Set(onAsArray(source.contactGroups).map((row) => String(row.id)));
        const notebookCollectionIds = new Set(onAsArray(source.notebookCollections).map((row) => String(row.id)));
        const guidePageIds = new Set(onAsArray(source.guidePages).map((row) => String(row.id)));
        const stepGroupIds = new Set(onAsArray(source.stepGroups).map((row) => String(row.id)));
        const libraryGroupIds = new Set(onAsArray(source.libraryGroups).map((row) => String(row.id)));
        const manualPageIds = new Set(onAsArray(source.manualPages).map((row) => String(row.id)));
        const courseModuleIds = new Set(onAsArray(source.courseModules).map((row) => String(row.id)));
        const snippetRepositoryIds = new Set(onAsArray(source.snippetRepositories).map((row) => String(row.id)));
        const snippetFolderIds = new Set(onAsArray(source.snippetFolders).map((row) => String(row.id)));
        const centerRunbookIds = new Set(onAsArray(source.centerRunbooks).map((row) => String(row.id)));

        return {
            ...source,
            credentials: onAsArray(source.credentials).filter((row) => !row.parentId || !credentialIds.has(String(row.parentId))),
            credentialFiles: onAsArray(source.credentialFiles).filter((row) => !credentialIds.has(String(row.credentialId))),
            companyVaultEntries: onAsArray(source.companyVaultEntries).filter((row) =>
                !companyGroupIds.has(String(row.groupId)) && (!row.parentId || !companyEntryIds.has(String(row.parentId)))),
            companyVaultFiles: onAsArray(source.companyVaultFiles).filter((row) =>
                !companyGroupIds.has(String(row.groupId)) && (!row.entryId || !companyEntryIds.has(String(row.entryId)))),
            personalVaultEntries: onAsArray(source.personalVaultEntries).filter((row) =>
                !personalGroupIds.has(String(row.groupId)) && (!row.parentId || !personalEntryIds.has(String(row.parentId)))),
            personalVaultFiles: onAsArray(source.personalVaultFiles).filter((row) =>
                !personalGroupIds.has(String(row.groupId)) && (!row.entryId || !personalEntryIds.has(String(row.entryId)))),
            wishlistItems: onAsArray(source.wishlistItems).filter((row) => !wishlistCategoryIds.has(String(row.categoryId))),
            contacts: onAsArray(source.contacts).filter((row) => !row.groupId || !contactGroupIds.has(String(row.groupId))),
            notebookEntries: onAsArray(source.notebookEntries).filter((row) => !row.collectionId || !notebookCollectionIds.has(String(row.collectionId))),
            guidePages: onAsArray(source.guidePages).filter((row) => !row.parentId || !guidePageIds.has(String(row.parentId))),
            steps: onAsArray(source.steps).filter((row) => !stepGroupIds.has(String(row.groupId))),
            libraryDocuments: onAsArray(source.libraryDocuments).filter((row) => !libraryGroupIds.has(String(row.groupId))),
            manualPages: onAsArray(source.manualPages).filter((row) => !row.parentId || !manualPageIds.has(String(row.parentId))),
            courseLessons: onAsArray(source.courseLessons).filter((row) => !row.moduleId || !courseModuleIds.has(String(row.moduleId))),
            courseTasks: onAsArray(source.courseTasks).filter((row) => !row.moduleId || !courseModuleIds.has(String(row.moduleId))),
            courseSchedule: onAsArray(source.courseSchedule).filter((row) => !row.moduleId || !courseModuleIds.has(String(row.moduleId))),
            snippetFolders: onAsArray(source.snippetFolders).filter((row) =>
                !snippetRepositoryIds.has(String(row.repositoryId)) && (!row.parentId || !snippetFolderIds.has(String(row.parentId)))),
            snippetFiles: onAsArray(source.snippetFiles).filter((row) =>
                !snippetRepositoryIds.has(String(row.repositoryId)) && (!row.folderId || !snippetFolderIds.has(String(row.folderId)))),
            centerRunbookSteps: onAsArray(source.centerRunbookSteps).filter((row) => !centerRunbookIds.has(String(row.runbookId))),
        };
    }, [useExtraArchive]);

    const GROUPS = [
        {
            key: "companies", title: "Compañías", icon: <FaBuilding />, items: COMPANIES ?? [],
            restore: COMPANIES_CONSTANTS.RESTORE_ONE, remove: COMPANIES_CONSTANTS.REMOVE, mutate: mutateCompanies,
            search: (item) => [item.name, item.description],
            getDetail: (item) => `${item.projects?.length ?? 0} proyectos dentro`,
            warning: "Se van también sus proyectos y subproyectos.",
        },
        {
            key: "projects", title: "Proyectos", icon: <FaFolderOpen />, items: PROJECTS ?? [],
            restore: PROJECTS_CONSTANTS.RESTORE_ONE, remove: PROJECTS_CONSTANTS.REMOVE, mutate: mutateProjects,
            search: (item) => [item.name, item.company?.name],
            getDetail: (item) => `${item.company?.name ?? "Sin compañía"} · ${item.subProjects?.length ?? 0} subproyectos`,
            warning: "Se van también sus subproyectos, bitácora y credenciales.",
        },
        {
            key: "subprojects", title: "Subproyectos", icon: <FaLayerGroup />, items: SUBPROJECTS ?? [],
            restore: SUBPROJECTS_CONSTANTS.RESTORE_ONE, remove: SUBPROJECTS_CONSTANTS.REMOVE, mutate: mutateSubProjects,
            search: (item) => [item.name, item.project?.name],
            getDetail: (item) => `${item.project?.name ?? "Sin proyecto"} · ${item.type || "Sin tipo"}`,
            warning: "Se van también su bitácora, tareas, entornos, archivos y grupos de guías.",
        },
        {
            key: "logbook", title: "Entradas de bitácora", icon: <FaBook />,
            items: (LOGBOOK ?? []).map((item) => ({ ...item, name: item.title })),
            restore: LOGBOOK_CONSTANTS.RESTORE_ONE, remove: LOGBOOK_CONSTANTS.REMOVE, mutate: mutateLogbook,
            search: (item) => [item.name, item.subProject?.name],
            getDetail: (item) => `${item.subProject?.project?.name ?? "Sin proyecto"} · ${item.subProject?.name ?? ""}`,
            warning: "Se van también sus tareas, adjuntos y cambios de código.",
        },
        {
            key: "manuals", title: "Manuales", icon: <FaBookOpen />, items: MANUALS ?? [],
            restore: MANUALS_CONSTANTS.RESTORE_ONE, remove: MANUALS_CONSTANTS.REMOVE, mutate: mutateManuals,
            search: (item) => [item.name, item.description, item.technology],
            getDetail: (item) => `${item.pageCount ?? 0} páginas · ${item.technology || item.category || "General"}`,
            warning: "Se van también todas sus páginas y subpáginas.",
        },
        {
            key: "guides", title: "Grupos de guías", icon: <FaListOl />, items: GUIDES ?? [],
            restore: GUIDES_CONSTANTS.RESTORE_ONE, remove: GUIDES_CONSTANTS.REMOVE, mutate: mutateGuides,
            search: (item) => [item.name, item.description, item.project?.name, item.subProject?.name],
            getDetail: (item) => `${item.project?.name ?? "Sin proyecto"} · ${item.subProject?.name ?? "Nivel proyecto"} · ${item.pageCount ?? 0} guías`,
            warning: "Se van también todas las guías y subguías del grupo.",
        },
        {
            key: "courses", title: "Cursos", icon: <FaBookOpen />, items: (COURSES ?? []).map((item) => ({ ...item, name: item.title })),
            restore: COURSES_CONSTANTS.RESTORE_ONE, remove: COURSES_CONSTANTS.REMOVE, mutate: mutateCourses,
            search: (item) => [item.title, item.description, item.platform, item.category],
            getDetail: (item) => `${item.status || "Sin estado"} · ${item.platform || "Cuaderno docente"}`,
            warning: "Se van también sus módulos, apuntes, tareas e itinerario.",
        },
        {
            key: "centerInbox", isExtra: true, title: "Organización · Inbox", icon: <FaStickyNote />, items: onAsArray(EXTRA.centerInbox).map((item) => ({ ...item, name: item.title || "Captura" })),
            restore: CENTER_CONSTANTS.INBOX_RESTORE, remove: CENTER_CONSTANTS.INBOX_REMOVE,
            search: (item) => [item.title, item.content, item.kind], getDetail: (item) => item.kind || "Captura rápida",
        },
        {
            key: "centerReminders", isExtra: true, title: "Organización · recordatorios", icon: <FaTasks />, items: onAsArray(EXTRA.centerReminders).map((item) => ({ ...item, name: item.title || "Recordatorio" })),
            restore: CENTER_CONSTANTS.REMINDER_RESTORE, remove: CENTER_CONSTANTS.REMINDER_REMOVE,
            search: (item) => [item.title, item.description, item.notes], getDetail: (item) => item.remindAt ? `Aviso: ${item.remindAt}` : "Recordatorio",
        },
        {
            key: "centerTemplates", isExtra: true, title: "Organización · plantillas", icon: <FaLayerGroup />, items: onAsArray(EXTRA.centerTemplates).map((item) => ({ ...item, name: item.name || "Plantilla" })),
            restore: CENTER_CONSTANTS.TEMPLATE_RESTORE, remove: CENTER_CONSTANTS.TEMPLATE_REMOVE,
            search: (item) => [item.name, item.description, item.templateType], getDetail: (item) => item.templateType || "Plantilla",
        },
        {
            key: "centerRunbooks", isExtra: true, title: "Organización · runbooks", icon: <FaListOl />, items: onAsArray(EXTRA.centerRunbooks).map((item) => ({ ...item, name: item.name || "Runbook" })),
            restore: CENTER_CONSTANTS.RUNBOOK_RESTORE, remove: CENTER_CONSTANTS.RUNBOOK_REMOVE,
            search: (item) => [item.name, item.description, item.category], getDetail: (item) => item.category || "Runbook",
            warning: "Se van también todos sus pasos archivados.",
        },
        {
            key: "centerRunbookSteps", isExtra: true, title: "Organización · pasos de runbook", icon: <FaListOl />, items: onAsArray(EXTRA.centerRunbookSteps).map((item) => ({ ...item, name: item.title || "Paso de runbook" })),
            restore: CENTER_CONSTANTS.RUNBOOK_STEP_RESTORE, remove: CENTER_CONSTANTS.RUNBOOK_STEP_REMOVE,
            search: (item) => [item.title, item.description, item.command, item.notes], getDetail: () => "Paso de runbook",
        },
        {
            key: "globalTags", isExtra: true, title: "Organización · etiquetas globales", icon: <FaLayerGroup />, items: onAsArray(EXTRA.globalTags).map((item) => ({ ...item, name: item.name || "Etiqueta global" })),
            restore: CENTER_CONSTANTS.GLOBAL_TAG_RESTORE, remove: CENTER_CONSTANTS.GLOBAL_TAG_REMOVE,
            search: (item) => [item.name, item.code, item.description], getDetail: (item) => item.description || item.code || "Etiqueta global",
        },
        {
            key: "libraryGroups", isExtra: true, title: "Biblioteca · grupos", icon: <FaBookOpen />, items: onAsArray(EXTRA.libraryGroups).map((item) => ({ ...item, name: item.name || "Grupo de biblioteca" })),
            restore: LIBRARY_CONSTANTS.GROUP_RESTORE, remove: LIBRARY_CONSTANTS.GROUP_REMOVE,
            search: (item) => [item.name, item.description], getDetail: (item) => item.description || "Grupo de biblioteca",
            warning: "Se van también los documentos archivados dentro del grupo.",
        },
        {
            key: "libraryDocuments", isExtra: true, title: "Biblioteca · documentos", icon: <FaFileAlt />, items: onAsArray(EXTRA.libraryDocuments).map((item) => ({ ...item, name: item.title || item.originalName || item.name || "Documento" })),
            restore: LIBRARY_CONSTANTS.PDF_RESTORE, remove: LIBRARY_CONSTANTS.PDF_REMOVE,
            search: (item) => [item.name, item.author, item.category], getDetail: (item) => [item.author, item.category].filter(Boolean).join(" · ") || "Documento de biblioteca",
        },
        {
            key: "tags", isExtra: true, title: "Etiquetas", icon: <FaLayerGroup />, items: onAsArray(EXTRA.tags).map((item) => ({ ...item, name: item.name || "Etiqueta" })),
            restore: TAGS_CONSTANTS.RESTORE_ONE, remove: TAGS_CONSTANTS.REMOVE,
            search: (item) => [item.name, item.description, item.color], getDetail: (item) => item.description || "Etiqueta",
        },
        {
            key: "manualPages", isExtra: true, title: "Manuales · páginas", icon: <FaBookOpen />, items: onAsArray(EXTRA.manualPages).map((item) => ({ ...item, name: item.title || "Página de manual" })),
            restore: MANUALS_CONSTANTS.PAGE_RESTORE, remove: MANUALS_CONSTANTS.PAGE_REMOVE,
            search: (item) => [item.title, item.content], getDetail: () => "Página de manual",
            warning: "Si tiene subpáginas archivadas, se procesan junto con ella.",
        },
        {
            key: "manualSections", isExtra: true, title: "Manuales · secciones heredadas", icon: <FaListOl />, items: onAsArray(EXTRA.manualSections).map((item) => ({ ...item, name: item.title || item.name || "Sección" })),
            restore: MANUALS_CONSTANTS.SECTION_RESTORE, remove: MANUALS_CONSTANTS.SECTION_REMOVE,
            search: (item) => [item.title, item.name, item.description], getDetail: () => "Sección heredada de manual",
        },
        {
            key: "manualEntries", isExtra: true, title: "Manuales · entradas heredadas", icon: <FaBook />, items: onAsArray(EXTRA.manualEntries).map((item) => ({ ...item, name: item.title || item.name || "Entrada" })),
            restore: MANUALS_CONSTANTS.ENTRY_RESTORE, remove: MANUALS_CONSTANTS.ENTRY_REMOVE,
            search: (item) => [item.title, item.name, item.content], getDetail: () => "Entrada heredada de manual",
        },
        {
            key: "courseModules", isExtra: true, title: "Cursos · módulos", icon: <FaBookOpen />, items: onAsArray(EXTRA.courseModules).map((item) => ({ ...item, name: item.title || item.name || "Módulo" })),
            restore: COURSES_CONSTANTS.MODULE_RESTORE, remove: COURSES_CONSTANTS.MODULE_REMOVE,
            search: (item) => [item.title, item.name, item.description], getDetail: () => "Módulo de curso",
            warning: "Se van también sus lecciones, tareas y elementos de itinerario archivados.",
        },
        {
            key: "courseLessons", isExtra: true, title: "Cursos · lecciones", icon: <FaBook />, items: onAsArray(EXTRA.courseLessons).map((item) => ({ ...item, name: item.title || item.name || "Lección" })),
            restore: COURSES_CONSTANTS.LESSON_RESTORE, remove: COURSES_CONSTANTS.LESSON_REMOVE,
            search: (item) => [item.title, item.name, item.description], getDetail: () => "Lección de curso",
        },
        {
            key: "courseTasks", isExtra: true, title: "Cursos · tareas", icon: <FaTasks />, items: onAsArray(EXTRA.courseTasks).map((item) => ({ ...item, name: item.title || item.name || "Tarea" })),
            restore: COURSES_CONSTANTS.TASK_RESTORE, remove: COURSES_CONSTANTS.TASK_REMOVE,
            search: (item) => [item.title, item.name, item.description, item.status], getDetail: (item) => item.status || "Tarea de curso",
        },
        {
            key: "courseSchedule", isExtra: true, title: "Cursos · itinerario", icon: <FaListOl />, items: onAsArray(EXTRA.courseSchedule).map((item) => ({ ...item, name: item.title || item.name || "Elemento de itinerario" })),
            restore: COURSES_CONSTANTS.SCHEDULE_RESTORE, remove: COURSES_CONSTANTS.SCHEDULE_REMOVE,
            search: (item) => [item.title, item.name, item.description, item.kind], getDetail: (item) => item.kind || "Itinerario de curso",
        },
        {
            key: "savingsAccounts", isExtra: true, title: "Ahorros · cuentas", icon: <FaFolderOpen />, items: onAsArray(EXTRA.savingsAccounts).map((item) => ({ ...item, name: item.name || "Cuenta" })),
            restore: SAVINGS_CONSTANTS.ACCOUNT_RESTORE, remove: SAVINGS_CONSTANTS.ACCOUNT_REMOVE,
            search: (item) => [item.name, item.type, item.currency], getDetail: (item) => [item.type, item.currency].filter(Boolean).join(" · ") || "Cuenta de ahorro",
        },
        {
            key: "savingsTransactions", isExtra: true, title: "Ahorros · movimientos", icon: <FaListOl />, items: onAsArray(EXTRA.savingsTransactions).map((item) => ({ ...item, name: item.description || item.concept || item.type || "Movimiento" })),
            restore: SAVINGS_CONSTANTS.TRANSACTION_RESTORE, remove: SAVINGS_CONSTANTS.TRANSACTION_REMOVE,
            search: (item) => [item.name, item.type, item.category, item.currency], getDetail: (item) => [item.type, item.category, item.amount != null ? String(item.amount) : null].filter(Boolean).join(" · ") || "Movimiento de ahorro",
        },
        {
            key: "savingsGoals", isExtra: true, title: "Ahorros · objetivos", icon: <FaHeart />, items: onAsArray(EXTRA.savingsGoals).map((item) => ({ ...item, name: item.name || item.title || "Objetivo" })),
            restore: SAVINGS_CONSTANTS.GOAL_RESTORE, remove: SAVINGS_CONSTANTS.GOAL_REMOVE,
            search: (item) => [item.name, item.title, item.description, item.currency], getDetail: (item) => item.currency || "Objetivo de ahorro",
        },
        {
            key: "savingsGoalMovements", isExtra: true, title: "Ahorros · movimientos de objetivos", icon: <FaListOl />, items: onAsArray(EXTRA.savingsGoalMovements).map((item) => ({ ...item, name: item.note || item.description || "Movimiento de objetivo" })),
            restore: SAVINGS_CONSTANTS.GOAL_MOVEMENT_RESTORE, remove: SAVINGS_CONSTANTS.GOAL_MOVEMENT_REMOVE,
            search: (item) => [item.name, item.type, item.amount], getDetail: (item) => item.amount != null ? String(item.amount) : "Movimiento de objetivo",
        },
        {
            key: "savingsBudgets", isExtra: true, title: "Ahorros · presupuestos", icon: <FaFolderOpen />, items: onAsArray(EXTRA.savingsBudgets).map((item) => ({ ...item, name: item.name || item.category || "Presupuesto" })),
            restore: SAVINGS_CONSTANTS.BUDGET_RESTORE, remove: SAVINGS_CONSTANTS.BUDGET_REMOVE,
            search: (item) => [item.name, item.category, item.currency], getDetail: (item) => [item.category, item.currency].filter(Boolean).join(" · ") || "Presupuesto",
        },
        {
            key: "monthlyEntries", isExtra: true, title: "Finanzas mensuales · registros", icon: <FaListOl />, items: onAsArray(EXTRA.monthlyEntries).map((item) => ({ ...item, name: item.description || item.concept || item.category || "Registro mensual" })),
            restore: MONTHLY_FINANCE_CONSTANTS.ENTRY_RESTORE, remove: MONTHLY_FINANCE_CONSTANTS.ENTRY_REMOVE,
            search: (item) => [item.name, item.category, item.type], getDetail: (item) => [item.type, item.category, item.amount != null ? String(item.amount) : null].filter(Boolean).join(" · ") || "Registro mensual",
        },
        {
            key: "monthlyRecurring", isExtra: true, title: "Finanzas mensuales · recurrentes", icon: <FaUndo />, items: onAsArray(EXTRA.monthlyRecurring).map((item) => ({ ...item, name: item.description || item.concept || item.category || "Recurrente" })),
            restore: MONTHLY_FINANCE_CONSTANTS.RECURRING_RESTORE, remove: MONTHLY_FINANCE_CONSTANTS.RECURRING_REMOVE,
            search: (item) => [item.name, item.category, item.type], getDetail: (item) => [item.type, item.category, item.amount != null ? String(item.amount) : null].filter(Boolean).join(" · ") || "Movimiento recurrente",
        },
        {
            key: "monthlyBudgets", isExtra: true, title: "Finanzas mensuales · presupuestos", icon: <FaFolderOpen />, items: onAsArray(EXTRA.monthlyBudgets).map((item) => ({ ...item, name: item.name || item.category || "Presupuesto mensual" })),
            restore: MONTHLY_FINANCE_CONSTANTS.BUDGET_RESTORE, remove: MONTHLY_FINANCE_CONSTANTS.BUDGET_REMOVE,
            search: (item) => [item.name, item.category], getDetail: (item) => item.category || "Presupuesto mensual",
        },
        {
            key: "snippetRepositories", isExtra: true, title: "Snippets · repositorios", icon: <FaCode />, items: onAsArray(EXTRA.snippetRepositories).map((item) => ({ ...item, name: item.name || "Repositorio" })),
            restore: CODE_REPOSITORY_CONSTANTS.REPOSITORY_RESTORE, remove: CODE_REPOSITORY_CONSTANTS.REPOSITORY_REMOVE,
            search: (item) => [item.name, item.description, item.language], getDetail: (item) => item.description || item.language || "Repositorio de snippets",
            warning: "Se van también sus carpetas y archivos archivados.",
        },
        {
            key: "snippetFolders", isExtra: true, title: "Snippets · carpetas", icon: <FaFolderOpen />, items: onAsArray(EXTRA.snippetFolders).map((item) => ({ ...item, name: item.name || "Carpeta" })),
            restore: CODE_REPOSITORY_CONSTANTS.FOLDER_RESTORE, remove: CODE_REPOSITORY_CONSTANTS.FOLDER_REMOVE,
            search: (item) => [item.name], getDetail: () => "Carpeta de snippets",
            warning: "Si tiene subcarpetas y archivos archivados, se procesan junto con ella.",
        },
        {
            key: "snippetFiles", isExtra: true, title: "Snippets · archivos", icon: <FaFileAlt />, items: onAsArray(EXTRA.snippetFiles).map((item) => ({ ...item, name: item.name || item.originalName || "Archivo" })),
            restore: CODE_REPOSITORY_CONSTANTS.FILE_RESTORE, remove: CODE_REPOSITORY_CONSTANTS.FILE_REMOVE,
            search: (item) => [item.name, item.language, item.extension], getDetail: (item) => item.language || item.extension || "Archivo de snippets",
        },
        {
            key: "recoveryCodes", isExtra: true, title: "Bóveda personal · códigos de recuperación", icon: <FaKey />, items: onAsArray(EXTRA.recoveryCodes).map((item) => ({ ...item, name: item.label || `Código ${item.position ?? ""}`.trim() })),
            restore: PERSONAL_VAULT_CONSTANTS.RECOVERY_CODE_RESTORE, remove: PERSONAL_VAULT_CONSTANTS.RECOVERY_CODE_REMOVE,
            search: (item) => [item.name, item.label, item.entryTitle], getDetail: (item) => item.entryTitle ? `Acceso: ${item.entryTitle}` : "Código de recuperación",
        },
        {
            key: "wishlistCategories", isExtra: true, title: "Wish List · categorías", icon: <FaHeart />, items: onAsArray(EXTRA.wishlistCategories).map((item) => ({ ...item, name: item.name || "Categoría" })),
            restore: WISHLIST_CATEGORY_CONSTANTS.RESTORE_ONE, remove: WISHLIST_CATEGORY_CONSTANTS.REMOVE,
            search: (item) => [item.name, item.description], getDetail: () => "Categoría de Wish List",
            warning: "Se van también sus artículos.",
        },
        {
            key: "wishlistItems", isExtra: true, title: "Wish List · artículos", icon: <FaShoppingBag />, items: onAsArray(EXTRA.wishlistItems).map((item) => ({ ...item, name: item.name || item.title || "Artículo" })),
            restore: WISHLIST_ITEM_CONSTANTS.RESTORE_ONE, remove: WISHLIST_ITEM_CONSTANTS.REMOVE,
            search: (item) => [item.name, item.store, item.url], getDetail: (item) => [item.store, item.price != null ? `${item.currency || "ARS"} ${item.price}` : null].filter(Boolean).join(" · ") || "Artículo de Wish List",
        },
        {
            key: "credentials", isExtra: true, title: "Credenciales", icon: <FaKey />, items: onAsArray(EXTRA.credentials).map((item) => ({ ...item, name: item.title || "Credencial" })),
            restore: CREDENTIALS_CONSTANTS.RESTORE_ONE, remove: CREDENTIALS_CONSTANTS.REMOVE,
            search: (item) => [item.title, item.type, item.host, item.username], getDetail: (item) => `${item.type || "Credencial"}${item.host ? ` · ${item.host}` : ""}`,
            warning: "Se van también sus credenciales anidadas y archivos.",
        },
        {
            key: "credentialFiles", isExtra: true, title: "Archivos de credenciales", icon: <FaFileAlt />, items: onAsArray(EXTRA.credentialFiles).map((item) => ({ ...item, name: item.originalName || item.title || "Archivo" })),
            restore: CREDENTIALS_CONSTANTS.FILE_RESTORE, remove: CREDENTIALS_CONSTANTS.FILE_REMOVE,
            search: (item) => [item.name, item.purpose, item.mimetype], getDetail: (item) => item.purpose || item.mimetype || "Archivo de credencial",
        },
        {
            key: "documents", isExtra: true, title: "Archivos de proyectos", icon: <FaFileAlt />, items: onAsArray(EXTRA.documents).map((item) => ({ ...item, name: item.originalName || item.title || "Archivo" })),
            restore: DOCUMENTS_CONSTANTS.RESTORE_ONE, remove: DOCUMENTS_CONSTANTS.REMOVE,
            search: (item) => [item.name, item.mimetype], getDetail: (item) => item.mimetype || "Archivo",
        },
        {
            key: "environments", isExtra: true, title: "Entornos", icon: <FaServer />, items: onAsArray(EXTRA.environments).map((item) => ({ ...item, name: item.name || "Entorno" })),
            restore: ENVIRONMENTS_CONSTANTS.RESTORE_ONE, remove: ENVIRONMENTS_CONSTANTS.REMOVE,
            search: (item) => [item.name, item.code, item.baseUrl, item.apiUrl], getDetail: (item) => item.code || "Entorno",
        },
        {
            key: "snippets", isExtra: true, title: "Scripts / snippets de proyecto", icon: <FaCode />, items: onAsArray(EXTRA.snippets).map((item) => ({ ...item, name: item.title || "Script" })),
            restore: SNIPPETS_CONSTANTS.RESTORE_ONE, remove: SNIPPETS_CONSTANTS.REMOVE,
            search: (item) => [item.title, item.language, item.description], getDetail: (item) => item.language || "Snippet",
        },
        {
            key: "drafts", isExtra: true, title: "Borradores", icon: <FaStickyNote />, items: onAsArray(EXTRA.drafts).map((item) => ({ ...item, name: item.title || "Borrador" })),
            restore: DRAFTS_CONSTANTS.RESTORE_ONE, remove: DRAFTS_CONSTANTS.REMOVE,
            search: (item) => [item.title, item.content, item.origin], getDetail: (item) => item.origin || "Borrador",
        },
        {
            key: "links", isExtra: true, title: "Enlaces", icon: <FaLink />, items: onAsArray(EXTRA.links).map((item) => ({ ...item, name: item.title || item.url || "Enlace" })),
            restore: LINKS_CONSTANTS.RESTORE_ONE, remove: LINKS_CONSTANTS.REMOVE,
            search: (item) => [item.title, item.url, item.category], getDetail: (item) => item.category || item.url || "Enlace",
        },
        {
            key: "changes", isExtra: true, title: "Cambios de código", icon: <FaCodeBranch />, items: onAsArray(EXTRA.changes).map((item) => ({ ...item, name: item.filePath || "Cambio de código" })),
            restore: CHANGES_CONSTANTS.RESTORE_ONE, remove: CHANGES_CONSTANTS.REMOVE,
            search: (item) => [item.filePath, item.language, item.action, item.description], getDetail: (item) => `${item.action || "Cambio"} · ${item.language || "Código"}`,
        },
        {
            key: "stepGroups", isExtra: true, title: "Pasos · grupos", icon: <FaListOl />, items: onAsArray(EXTRA.stepGroups).map((item) => ({ ...item, name: item.name || "Grupo de pasos" })),
            restore: STEP_GROUPS_CONSTANTS.RESTORE_ONE, remove: STEP_GROUPS_CONSTANTS.REMOVE,
            search: (item) => [item.name, item.description, item.category], getDetail: (item) => item.description || item.category || "Grupo de pasos",
            warning: "Se van también todos los pasos archivados dentro del grupo.",
        },
        {
            key: "steps", isExtra: true, title: "Pasos archivados", icon: <FaListOl />, items: onAsArray(EXTRA.steps).map((item) => ({ ...item, name: item.title || "Paso" })),
            restore: STEPS_CONSTANTS.RESTORE_ONE, remove: STEPS_CONSTANTS.REMOVE,
            search: (item) => [item.title, item.description, item.command, item.type], getDetail: (item) => item.type || "Paso",
        },
        {
            key: "kanbanColumns", isExtra: true, title: "Trello · columnas", icon: <FaColumns />, items: onAsArray(EXTRA.kanbanColumns).map((item) => ({ ...item, name: item.name || "Columna" })),
            restore: KANBAN_COLUMNS_CONSTANTS.RESTORE_ONE, remove: KANBAN_COLUMNS_CONSTANTS.REMOVE,
            search: (item) => [item.name, item.description], getDetail: (item) => item.description || "Columna del tablero",
            warning: "Se van también las tarjetas archivadas dentro de la columna.",
        },
        {
            key: "kanbanCards", isExtra: true, title: "Trello · tarjetas", icon: <FaTasks />, items: onAsArray(EXTRA.kanbanCards).map((item) => ({ ...item, name: item.title || "Tarjeta" })),
            restore: KANBAN_CARDS_CONSTANTS.RESTORE_ONE, remove: KANBAN_CARDS_CONSTANTS.REMOVE,
            search: (item) => [item.title, item.description, item.priority, ...(Array.isArray(item.labels) ? item.labels : [])], getDetail: (item) => item.priority || "Tarjeta del tablero",
            warning: "Al eliminar definitivamente una tarjeta también se eliminan sus adjuntos físicos.",
        },
        {
            key: "kanbanCardFiles", isExtra: true, title: "Trello · adjuntos", icon: <FaPaperclip />, items: onAsArray(EXTRA.kanbanCardFiles).map((item) => ({ ...item, name: item.originalName || item.title || "Adjunto" })),
            restore: KANBAN_CARD_FILES_CONSTANTS.RESTORE_ONE, remove: KANBAN_CARD_FILES_CONSTANTS.REMOVE,
            search: (item) => [item.originalName, item.title, item.mimetype], getDetail: (item) => item.mimetype || "Adjunto de tarjeta",
        },
        {
            key: "todos", isExtra: true, title: "Tareas de proyecto", icon: <FaTasks />, items: onAsArray(EXTRA.todos).map((item) => ({ ...item, name: item.title || "Tarea" })),
            restore: TODOS_CONSTANTS.RESTORE_ONE, remove: TODOS_CONSTANTS.REMOVE,
            search: (item) => [item.title, item.description, item.priority], getDetail: (item) => item.priority || "Tarea",
        },
        {
            key: "guidePages", isExtra: true, title: "Guías archivadas", icon: <FaBookOpen />, items: onAsArray(EXTRA.guidePages).map((item) => ({ ...item, name: item.title || "Guía" })),
            restore: GUIDES_CONSTANTS.PAGE_RESTORE, remove: GUIDES_CONSTANTS.PAGE_REMOVE,
            search: (item) => [item.title, item.content], getDetail: () => "Página de guía",
            warning: "Si tiene subguías archivadas, se procesan junto con ella.",
        },
        {
            key: "companyVaultGroups", isExtra: true, title: "Bóveda de compañías · grupos", icon: <FaShieldAlt />, items: onAsArray(EXTRA.companyVaultGroups).map((item) => ({ ...item, name: item.name || "Grupo de bóveda" })),
            restore: VAULT_CONSTANTS.GROUP_RESTORE, remove: VAULT_CONSTANTS.GROUP_REMOVE,
            search: (item) => [item.name, item.description, item.kind], getDetail: (item) => item.kind || "Grupo de bóveda",
            warning: "Se van también sus accesos y archivos.",
        },
        {
            key: "companyVaultEntries", isExtra: true, title: "Bóveda de compañías · accesos", icon: <FaKey />, items: onAsArray(EXTRA.companyVaultEntries).map((item) => ({ ...item, name: item.title || "Acceso" })),
            restore: VAULT_CONSTANTS.ENTRY_RESTORE, remove: VAULT_CONSTANTS.ENTRY_REMOVE,
            search: (item) => [item.title, item.type, item.host, item.username], getDetail: (item) => item.type || "Acceso",
            warning: "Se van también sus accesos anidados y archivos.",
        },
        {
            key: "companyVaultFiles", isExtra: true, title: "Bóveda de compañías · archivos", icon: <FaFileAlt />, items: onAsArray(EXTRA.companyVaultFiles).map((item) => ({ ...item, name: item.originalName || item.title || "Archivo" })),
            restore: VAULT_CONSTANTS.FILE_RESTORE, remove: VAULT_CONSTANTS.FILE_REMOVE,
            search: (item) => [item.name, item.mimetype], getDetail: (item) => item.mimetype || "Archivo",
        },
        {
            key: "personalVaultGroups", isExtra: true, title: "Bóveda personal · grupos", icon: <FaShieldAlt />, items: onAsArray(EXTRA.personalVaultGroups).map((item) => ({ ...item, name: item.name || "Grupo personal" })),
            restore: PERSONAL_VAULT_CONSTANTS.GROUP_RESTORE, remove: PERSONAL_VAULT_CONSTANTS.GROUP_REMOVE,
            search: (item) => [item.name, item.description, item.kind], getDetail: (item) => item.kind || "Grupo personal",
            warning: "Se van también sus accesos y archivos.",
        },
        {
            key: "personalVaultEntries", isExtra: true, title: "Bóveda personal · accesos", icon: <FaKey />, items: onAsArray(EXTRA.personalVaultEntries).map((item) => ({ ...item, name: item.title || "Acceso personal" })),
            restore: PERSONAL_VAULT_CONSTANTS.ENTRY_RESTORE, remove: PERSONAL_VAULT_CONSTANTS.ENTRY_REMOVE,
            search: (item) => [item.title, item.type, item.host, item.username], getDetail: (item) => item.type || "Acceso personal",
            warning: "Se van también sus accesos anidados y archivos.",
        },
        {
            key: "personalVaultFiles", isExtra: true, title: "Bóveda personal · archivos", icon: <FaFileAlt />, items: onAsArray(EXTRA.personalVaultFiles).map((item) => ({ ...item, name: item.originalName || item.title || "Archivo" })),
            restore: PERSONAL_VAULT_CONSTANTS.FILE_RESTORE, remove: PERSONAL_VAULT_CONSTANTS.FILE_REMOVE,
            search: (item) => [item.name, item.mimetype], getDetail: (item) => item.mimetype || "Archivo",
        },
        {
            key: "notebookCollections", isExtra: true, title: "Cuaderno · colecciones", icon: <FaBookOpen />,
            items: onAsArray(EXTRA.notebookCollections).map((item) => ({ ...item, name: item.name || "Colección" })),
            restore: NOTEBOOK_COLLECTION_CONSTANTS.RESTORE_ONE, remove: NOTEBOOK_COLLECTION_CONSTANTS.REMOVE,
            search: (item) => [item.name, item.description],
            getDetail: (item) => item.description || "Colección de Cuaderno",
            warning: "Se procesan también los registros archivados dentro de la colección.",
        },
        {
            key: "notebookEntries", isExtra: true, title: "Cuaderno · registros", icon: <FaBook />,
            items: onAsArray(EXTRA.notebookEntries).map((item) => ({ ...item, name: item.title || "Registro" })),
            restore: NOTEBOOK_ENTRY_CONSTANTS.RESTORE_ONE, remove: NOTEBOOK_ENTRY_CONSTANTS.REMOVE,
            search: (item) => [item.title, item.subtitle, item.type, item.status, item.summary, item.author, ...(Array.isArray(item.tags) ? item.tags : [])],
            getDetail: (item) => [item.type, item.status, item.author].filter(Boolean).join(" · ") || "Registro de Cuaderno",
        },
        {
            key: "stickyNotes", isExtra: true, title: "Notas rápidas", icon: <FaStickyNote />,
            items: onAsArray(EXTRA.stickyNotes).map((item) => ({ ...item, name: item.title || (item.content ? String(item.content).replace(/<[^>]+>/g, " ").trim().slice(0, 80) : "Nota sin título") })),
            restore: STICKY_NOTES_CONSTANTS.RESTORE_ONE, remove: STICKY_NOTES_CONSTANTS.REMOVE,
            search: (item) => [item.title, item.content, item.color],
            getDetail: (item) => item.isPinned ? "Nota rápida · fijada" : "Nota rápida",
        },
        {
            key: "contactGroups", isExtra: true, title: "Contactos · grupos", icon: <FaAddressBook />,
            items: onAsArray(EXTRA.contactGroups).map((item) => ({ ...item, name: item.name || "Grupo de contactos" })),
            restore: CONTACT_GROUP_CONSTANTS.RESTORE_ONE, remove: CONTACT_GROUP_CONSTANTS.REMOVE,
            search: (item) => [item.name, item.description, item.icon, item.color],
            getDetail: (item) => item.description || "Grupo de contactos",
            warning: "Se procesan también los contactos archivados dentro del grupo.",
        },
        {
            key: "contacts", isExtra: true, title: "Contactos", icon: <FaAddressBook />,
            items: onAsArray(EXTRA.contacts).map((item) => ({
                ...item,
                name: item.displayName || item.nickname || item.organization || "Contacto",
            })),
            restore: CONTACTS_CONSTANTS.RESTORE_ONE, remove: CONTACTS_CONSTANTS.REMOVE,
            search: (item) => [
                item.displayName, item.nickname, item.organization, item.jobTitle, item.relationship,
                ...(Array.isArray(item.phones) ? item.phones.map((phone) => phone.number) : []),
                ...(Array.isArray(item.emails) ? item.emails.map((email) => email.email) : []),
                ...(Array.isArray(item.tags) ? item.tags : []),
            ],
            getDetail: (item) => [item.organization, item.contactType].filter(Boolean).join(" · ") || "Contacto privado",
        },
    ].map((group) => ({
        ...group,
        items: (group.items ?? [])
            .filter((item) => onMatchesRange(item, DATE_RANGE.useRange, "archivedAt"))
            .filter((item) => onMatchesSearch(useSearch, group.search(item))),
    }));

    const TOTAL = GROUPS.reduce((total, group) => total + group.items.length, 0);
    const LOADING_ANY = LOADING_COMPANIES || LOADING_PROJECTS || LOADING_SUBPROJECTS
        || LOADING_LOGBOOK || LOADING_MANUALS || LOADING_GUIDES || LOADING_COURSES || useExtraLoading;

    const EXPORT_COLUMNS = [
        { key: "type", label: "Tipo" },
        { key: "name", label: "Nombre" },
        { key: "detail", label: "Detalle" },
        { key: "archivedAt", label: "Archivado" },
    ];
    const EXPORT_ROWS = GROUPS.flatMap((group) => group.items.map((item) => ({
        type: group.title,
        name: item.name,
        detail: group.getDetail(item),
        archivedAt: onFormatDate(item.archivedAt),
    })));

    return (
        <ViewLayout
            header={<PageHeader title="Archivados"
                description="Todo lo que archivás queda acá hasta que decidas recuperarlo o eliminarlo definitivamente."
                icon={<FaArchive />} />}
            toolbar={<ViewToolbar
                filters={<SearchFilter value={useSearch} onChange={setSearch} placeholder="Buscar en archivados..." />}
                dateRange={DATE_RANGE} total={TOTAL}
                exportColumns={EXPORT_COLUMNS} exportRows={EXPORT_ROWS} exportName="archivados" />}>

            {LOADING_ANY && <Skeleton variant="box" height={180} count={2} />}

            {!LOADING_ANY && TOTAL === 0 && (
                <EmptyState icon={<FaArchive />} title="No hay nada archivado"
                    description="Los registros archivados de NOTEBOOK aparecen acá antes de poder eliminarse definitivamente." />
            )}

            {!LOADING_ANY && GROUPS.filter((group) => group.items.length > 0).map((group) => (
                <section key={group.key} className="LStudio__app_archive_group">
                    <div className="LStudio__app_archive_group_head">
                        {group.icon}
                        <span className="LStudio__app_archive_group_title">{group.title}</span>
                        <Badge tone="neutral" size="sm">{group.items.length}</Badge>
                    </div>

                    <div className="LStudio__app_archive_list">
                        {group.items.map((item) => (
                            <Card key={item.id} className="LStudio__app_archive_item">
                                <div className="LStudio__app_archive_item_text">
                                    <span className="LStudio__app_archive_item_name">{item.name}</span>
                                    <span className="LStudio__app_archive_item_meta">
                                        {group.getDetail(item)} · archivado el {onFormatDate(item.archivedAt)}
                                    </span>
                                </div>
                                <div className="LStudio__app_archive_item_actions">
                                    <AppButton size="sm" variant="soft" icon={<FaUndo />}
                                        onClick={() => onRestore(group, item)}>Recuperar</AppButton>
                                    <AppButton size="sm" variant="ghost" tone="danger" icon={<FaTrash />}
                                        onClick={() => onRequestPassword(group, item)}>Eliminar</AppButton>
                                </div>
                            </Card>
                        ))}
                    </div>
                </section>
            ))}

            <AppModal isOpen={!!pendingDelete} onClose={() => setPendingDelete(null)} size="sm"
                title="Confirmá con tu contraseña"
                description={`Vas a eliminar "${pendingDelete?.item?.name ?? ""}" para siempre.`}
                footer={
                    <>
                        <AppButton variant="ghost" tone="neutral" onClick={() => setPendingDelete(null)}>Cancelar</AppButton>
                        <AppButton tone="danger" isLoading={useIsChecking} onClick={onConfirmDelete}>
                            Eliminar
                        </AppButton>
                    </>
                }>
                <FieldInput id="confirmPassword" type="password" label="Tu contraseña"
                    placeholder="La contraseña de tu login" value={usePassword} error={usePasswordError}
                    onChange={(event) => { setPassword(event.target.value); setPasswordError(null); }}
                    onKeyDown={(event) => { if (event.key === "Enter") onConfirmDelete(); }} />
            </AppModal>
        </ViewLayout>
    );
};
